import React from "react";
import logo from "../assets/logo.png";
import styled from "styled-components";
import {
  Container,
  Nav,
  NavItem,
  NavDropdown,
  Button,
  Navbar,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightToBracket, faUser } from "@fortawesome/free-solid-svg-icons";
import { useResizeDetector } from "react-resize-detector";
import { signIn, signOut, useUser } from "../utils/auth";
import { useParticipation } from "../utils/participation";
import { Link, useLocation } from "../utils/router";
import { InternSwitch } from "../intern/InternSwitch";
import { useInternMode } from "../intern/mode";

const TopNavbar = styled(Navbar)`
  background-color: #f8f9fa;
  height: 35px;
  border-bottom: 1px solid #dee2e6;
  .quick-links {
    display: flex;
    align-items: center;
  }
  .right-links {
    display: flex;
    align-items: center;
  }
  @media (max-width: 767.98px) {
    display: none;
  }
`;

const LogoWrapper = styled.div`
  padding: 0.5rem;
`;

const NavContainer = styled(Nav)`
  // @media (min-width: 768px) and (max-width: 989.98px) {
  //   &.left-nav {
  //     margin-right: auto;
  //   }

  //   &.right-nav {
  //     margin-left: auto;
  //   }
  // }
`;

// Anmelden beziehungsweise das Konto in der Navigationsleiste.
//
// In P4 fiel der Link auf backend.lwzo.ch weg, und damit verschwand der
// einzige Einstieg zur Anmeldung aus der Kopfzeile — angemeldet wurde zwar
// angezeigt, abgemeldet aber nicht angeboten. Inhaltlich dasselbe wie unten
// im Footer (components/Footer.tsx): abgemeldet ein "Anmelden", angemeldet
// der Name. Anders als der Footer sagt die Leiste zusaetzlich ausdruecklich
// "Abmelden", statt den Namen selbst zur Schaltflaeche zu machen — im Kopf
// der Seite ist ein Klick auf den eigenen Namen kein erwartbarer Abmeldeweg.
function AccountNav({ variant }: { variant: "bar" | "menu" }) {
  const user = useUser();
  const participation = useParticipation();
  const intern = useInternMode();

  if (user === null) {
    return (
      <Nav.Link
        as="span"
        data-testid="nav-login"
        onClick={() => signIn()}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon icon={faRightToBracket} /> Anmelden
      </Nav.Link>
    );
  }

  // DER ADMIN-LINK STEHT NEBEN DEM NAMEN, NICHT IM KLAPPMENUE. Er lag bis
  // hierher als Eintrag IM Kontomenue — zwei Klicks fuer den Ort, an dem die
  // Geschaeftsstelle ihren halben Tag verbringt. Jetzt ist er ein eigener
  // Link davor.
  //
  // WARUM DAS OHNE EINE NEUE BREAKPOINT-REGEL AUSKOMMT: AccountNav wird an
  // genau zwei Stellen gerendert — in der oberen Leiste (variant="bar", die
  // Leiste selbst ist unter 768px per display:none aus) und im Burgermenue
  // (variant="menu", d-md-none). Ein Fragment mit dem Link VOR dem Dropdown
  // erbt beide Plaetze: in der Leiste ist .right-links display:flex, der Link
  // sitzt also direkt links neben dem Namen; im Burgermenue stapelt er sich
  // als eigene Zeile darueber.
  //
  // AUCH BEI „stale" SICHTBAR (Token von vor der ACR-Richtlinie, siehe
  // intern/mode.tsx): der Mensch IST berechtigt, und /admin erklaert ihm, was
  // fehlt. Ihn zu verstecken waere die Variante, die die Supportanfrage
  // erzeugt („bei mir fehlt der Knopf").
  //
  // NICHT hinter `participation.known`: Staff hat in aller Regel gar keine
  // Teilnahme, und der Link darf nicht auf eine Antwort warten, die ihn nichts
  // angeht.
  const adminLink =
    intern.availability === "yes" || intern.availability === "stale" ? (
      <Nav.Link as="span" className="me-2">
        <Link href="/admin">
          <span data-testid="nav-admin">Admin</span>
        </Link>
      </Nav.Link>
    ) : null;

  // AUS DEMSELBEN GRUND WIE DER ADMIN-LINK NEBEN DEM NAMEN, NICHT DARIN.
  // Er lag im Kontomenue, war also zwei Klicks entfernt — fuer die Seite, auf
  // der Teilnehmende ihre ganze Arbeit machen. Dieselbe Mechanik traegt ihn:
  // ein Fragment vor dem Dropdown erbt beide Plaetze, obere Leiste wie
  // Burgermenue, ohne eine neue Breakpoint-Regel.
  //
  // SICHTBAR NUR MIT TEILNAHME, unveraendert: ohne Filter fuehrte der Eintrag
  // ein Staff-Konto in eine Sackgasse, weil GetMyProject auth.Participant
  // verlangt. Solange /auth/whoami aussteht (known === false) wird NICHTS
  // gerendert statt eines Platzhalters — ein Link, der nachtraeglich
  // erscheint, verschiebt die Leiste unter dem Mauszeiger.
  const myProjectLink =
    participation.known && participation.hasParticipation ? (
      <Nav.Link as="span" className="me-2">
        <Link href="/mein-projekt">
          <span data-testid="nav-my-project">Mein Projekt</span>
        </Link>
      </Nav.Link>
    ) : null;

  return (
    <>
      {adminLink}
      {myProjectLink}
      <NavDropdown
        align="end"
        data-testid="nav-account"
        id={`account-dropdown-${variant}`}
        title={
          <span>
            <FontAwesomeIcon icon={faUser} /> {user.name}
          </span>
        }
      >
        {/* Kein zusaetzlicher Kopf mit der Mailadresse: das ID-Token traegt bei
            scope="openid groups profile" weder email noch preferred_username
            (nachgesehen am ausgestellten Token), der Kopf bliebe also leer.
            Wer angemeldet ist, steht ohnehin im Titel des Menues. */}

        <NavDropdown.Item
          as="span"
          data-testid="nav-logout"
          onClick={() => signOut()}
          style={{ cursor: "pointer" }}
        >
          Abmelden
        </NavDropdown.Item>
      </NavDropdown>
    </>
  );
}

function Navigation() {
  // Im Admin-Bereich gibt es keinen Intern-Schalter: dort ist ohnehin alles
  // editierbar, und ein Schalter suggeriert einen Unterschied, den es nicht
  // gibt (Entscheid E5, docs/02-konzept.md §8). Er gehoert auf die
  // OEFFENTLICHEN Seiten, wo man Einzelobjekte sieht. Der Admin-LINK bleibt
  // dagegen stehen — ein Link auf die Seite, auf der man ist, verwirrt weniger
  // als ein Element, das beim Betreten verschwindet.
  const location = useLocation();
  const onAdmin = location === "/admin" || location.startsWith("/admin/");

  const { width, ref } = useResizeDetector({
    handleHeight: false,
    refreshMode: "throttle",
    refreshRate: 100,
  });

  // 990 or 976
  const showProjectsDropdown = width ? width < 977 : false;

  return (
    <div ref={ref}>
      <TopNavbar bg="light" expand="md">
        <Container>
          <Nav className="quick-links">
            <Nav.Link as="span">
              <Link href="/besucher/lehrpersonen">Lehrpersonen</Link>
            </Nav.Link>
            <Nav.Link as="span">
              <Link href="/lehrbetriebe">Lehrbetriebe</Link>
            </Nav.Link>
            <Nav.Link as="span">
              <Link href="/medien">Medien</Link>
            </Nav.Link>
          </Nav>
          <Nav className="ms-auto right-links">
            {/* Der Intern-Schalter (docs/02-konzept.md §8.1). Er steht neben
                dem Konto, nicht in der Hauptnavigation: er ist eine
                Eigenschaft der SITZUNG, kein Ort. */}
            {!onAdmin && (
              <Nav.Link as="span" className="me-2">
                <InternSwitch />
              </Nav.Link>
            )}
            <AccountNav variant="bar" />
          </Nav>
        </Container>
      </TopNavbar>

      <Navbar bg="light" data-bs-theme="light" expand="md">
        <Container>
          <div className="d-md-none">
            <Nav.Link as="span">
              <Link href="/">
                <img src={logo} height="50" alt="logo" />
              </Link>
            </Nav.Link>
          </div>

          <Navbar.Toggle aria-controls="basic-navbar-nav" />

          <Navbar.Collapse
            className="justify-content-between"
            id="left-navbar-nav"
          >
            <NavContainer className="left-nav">
              <NavItem>
                <Nav.Link as="span">
                  <Link href="/">Home</Link>
                </Nav.Link>
              </NavItem>
              <NavDropdown title="Besucher" id="basic-nav-dropdown">
                <Link href="/besucher/allgemeine-informationen">
                  <NavDropdown.Item as="span">
                    Allgemeine Informationen
                  </NavDropdown.Item>
                </Link>
                <Link href="/besucher/eltern">
                  <NavDropdown.Item as="span">Eltern</NavDropdown.Item>
                </Link>
                <Link href="/besucher/jugendliche">
                  <NavDropdown.Item as="span">Schüler</NavDropdown.Item>
                </Link>
                <Link href="/besucher/lehrpersonen">
                  <NavDropdown.Item as="span">Lehrpersonen</NavDropdown.Item>
                </Link>
              </NavDropdown>
              <NavDropdown title="Ausstellende" id="lernende-dropdown">
                <Link href="/ausstellende/zeig-was-du-kannst">
                  <NavDropdown.Item as="span">
                    Zeig was du kannst!
                  </NavDropdown.Item>
                </Link>
                <Link href="/ausstellende/reglement">
                  <NavDropdown.Item as="span">Reglement</NavDropdown.Item>
                </Link>
                <Link href="/ausstellende/anmeldung">
                  <NavDropdown.Item as="span">Anmeldung</NavDropdown.Item>
                </Link>
                <Link href="/lehrbetriebe">
                  <NavDropdown.Item as="span">Lehrbetriebe</NavDropdown.Item>
                </Link>
                <Link href="/kurs-auftrittskompetenz">
                  <NavDropdown.Item as="span">Kurs</NavDropdown.Item>
                </Link>
              </NavDropdown>
              <NavDropdown title="Über uns" id="about-dropdown">
                <Link href="/ueber-uns/das-ist-der-lwzo">
                  <NavDropdown.Item as="span">
                    Das ist der LWZO
                  </NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/galerie">
                  <NavDropdown.Item as="span">Galerie</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/wir-suchen">
                  <NavDropdown.Item as="span">Wir suchen...</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/vereinsmaterial">
                  <NavDropdown.Item as="span">Vereinsmaterial</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/helfende-hande">
                  <NavDropdown.Item as="span">Helfende Hände</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/sponsoren">
                  <NavDropdown.Item as="span">Sponsoren</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/downloads">
                  <NavDropdown.Item as="span">Downloads</NavDropdown.Item>
                </Link>
                <Link href="/ueber-uns/kontakt">
                  <NavDropdown.Item as="span">Kontakt</NavDropdown.Item>
                </Link>
              </NavDropdown>
            </NavContainer>

            <LogoWrapper className="d-none d-md-block">
              <Nav.Link as="span">
                <Link href="/">
                  <img src={logo} height="50" alt="logo" />
                </Link>
              </Nav.Link>
            </LogoWrapper>

            <NavContainer className="right-nav">
              {showProjectsDropdown ? (
                <NavDropdown title="Projekte" id="projects-dropdown">
                  <Link href="/projekte">
                    <NavDropdown.Item as="span">Projekte</NavDropdown.Item>
                  </Link>
                  <Link href="/projekte/lageplan">
                    <NavDropdown.Item as="span">Lageplan</NavDropdown.Item>
                  </Link>
                  <Link href="/projekte/personen">
                    <NavDropdown.Item as="span">Ausstellende</NavDropdown.Item>
                  </Link>
                  <Link href="/projekte/mix-ansicht">
                    <NavDropdown.Item as="span">Mix-Ansicht</NavDropdown.Item>
                  </Link>
                </NavDropdown>
              ) : (
                <>
                  <NavItem>
                    <Nav.Link as="span">
                      <Link href="/projekte">Projekte</Link>
                    </Nav.Link>
                  </NavItem>
                  <NavItem>
                    <Nav.Link as="span">
                      <Link href="/projekte/lageplan">Lageplan</Link>
                    </Nav.Link>
                  </NavItem>
                  <NavItem>
                    <Nav.Link as="span">
                      <Link href="/projekte/personen">Ausstellende</Link>
                    </Nav.Link>
                  </NavItem>
                  <NavItem>
                    <Nav.Link as="span">
                      <Link href="/projekte/mix-ansicht">Mix-Ansicht</Link>
                    </Nav.Link>
                  </NavItem>
                </>
              )}
              {/* Auf schmalen Breiten ist die obere Leiste ausgeblendet
                  (TopNavbar, display: none). Der Intern-Schalter waere dort
                  unerreichbar - deshalb steht er im aufklappbaren Menue ein
                  zweites Mal. Beide zeigen denselben Zustand; der Modus lebt
                  in der Sitzung, nicht in der Komponente. */}
              {!onAdmin && (
                <NavItem className="d-md-none mt-2">
                  <InternSwitch />
                </NavItem>
              )}
              {/* Ebenso das Konto: die obere Leiste ist unter 768px
                  ausgeblendet (TopNavbar, display: none), und ohne diesen
                  zweiten Platz gaebe es auf dem Handy gar keine Anmeldung. */}
              <NavItem className="d-md-none">
                <AccountNav variant="menu" />
              </NavItem>
            </NavContainer>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  );
}

export default Navigation;
